import React from 'react';
import { IconButton, Skeleton, Stack, Tooltip } from '@mui/material';
import {
  DescriptionOutlined as RecordsIcon,
  InfoOutlined as ErrorsIcon,
  Refresh as RefreshIcon,
  StorageOutlined as VolumeIcon,
} from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';
import {
  MetricsDateRange,
  MetricsTrendPanel,
  ResourceMetricsData,
} from '@modules/monitor/resource-triage/components/MetricsTrendPanel';
import styles from './MetricsPanel.module.scss';

export type LifetimeMetrics = {
  records: string;
  volume: string;
  errors: string;
};

export type MetricsPanelProps = {
  lifetimeMetrics: LifetimeMetrics;
  trendData: ResourceMetricsData;
  start: string | Date;
  end: string | Date;
  onDatesChanged: (dates: MetricsDateRange) => void;
  loading?: boolean;
  hideRecords?: boolean;
  onRefresh?: () => void;
  title?: string;
  resourceType?: string;
  resourceId?: string | number;
};

type SummaryCard = {
  key: 'records' | 'volume' | 'errors';
  label: string;
  value: string;
  Icon: SvgIconComponent;
};

export const MetricsPanel = (props: MetricsPanelProps) => {
  const {
    lifetimeMetrics,
    trendData,
    start,
    end,
    onDatesChanged,
    loading = false,
    hideRecords = false,
    onRefresh,
    title,
    resourceType,
    resourceId,
  } = props;

  const cards: SummaryCard[] = [];

  if (!hideRecords) {
    cards.push({
      key: 'records',
      label: 'Total Records',
      value: lifetimeMetrics.records,
      Icon: RecordsIcon,
    });
  }

  cards.push(
    { key: 'volume', label: 'Data Volume', value: lifetimeMetrics.volume, Icon: VolumeIcon },
    { key: 'errors', label: 'Errors', value: lifetimeMetrics.errors, Icon: ErrorsIcon },
  );

  return (
    <Stack className={styles.panel}>
      <span className={styles.title}>Lifetime Numbers</span>
      <Stack
        className={styles.summary}
        direction="row"
        alignItems="stretch"
        justifyContent="space-between"
      >
        <Stack className={styles.cards} direction="row" alignItems="stretch">
          {cards.map((card) => (
            <div key={card.key} className={styles.card}>
              <div className={styles.cardHeader}>
                <span className={styles.cardIcon} aria-hidden>
                  <card.Icon fontSize="small" />
                </span>
                <span className={styles.cardLabel}>{card.label}</span>
              </div>
              {loading ? (
                <Skeleton
                  data-testid="metric-card-skeleton"
                  animation="wave"
                  variant="rounded"
                  width="60%"
                  height={40}
                  className={styles.cardSkeleton}
                />
              ) : (
                <span className={styles.cardValue}>{card.value}</span>
              )}
            </div>
          ))}
        </Stack>

        {onRefresh ? (
          <Tooltip title="Refresh metrics">
            <IconButton
              aria-label="Refresh metrics"
              size="small"
              className={styles.refresh}
              onClick={onRefresh}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>

      <div className={styles.trendSurface}>
        <MetricsTrendPanel
          data={trendData}
          loading={loading}
          start={start}
          end={end}
          onDatesChanged={onDatesChanged}
          hideRecords={hideRecords}
          title={title}
          resourceType={resourceType}
          resourceId={resourceId}
        />
      </div>
    </Stack>
  );
};

MetricsPanel.displayName = 'MetricsPanel';
